import { PlusCircleSVG, TrashBin } from 'common/assets';
import { TransferAccountTypeEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import Button from 'components/elements/buttons/button';
import { useFormState } from 'components/elements/form';
import BankAccountSelectInput from 'legacy-modules/master/bank-account/components/select-input';
import CashAccountSelectInput from 'legacy-modules/master/cash-account/components/select-input';
import {
  CardContainer,
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  OtherExpenseCreateFormType,
  OtherExpenseDetailCreateFormType,
} from './form-type';

export function ExpenseCard(props: {
  name?: `otherExpenses.${number}.`;
  onRemove?: () => void;
  isEdit?: boolean;
}) {
  const { name = '', isEdit } = props;
  const { t } = useTranslation();
  const accountTypeName = `${name}accountType` as const;
  const accountType = useWatch({
    name: accountTypeName,
  });

  const transactionAtInput = (
    <>
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={{
          width: 0,
          height: 0,
          outline: 'none',
          border: 'none',
          position: 'absolute',
        }}
      />
      <Input
        type="date-time"
        hasTime={false}
        label={t('common:date')}
        name={`${name}transactionAt`}
        disabled={isEdit}
      />
    </>
  );

  const accountTypeSelect = (
    <Input
      type="select"
      name={`${name}accountType`}
      label={t('common:account_type')}
      placeholder={t('common:account_type')}
      options={[
        {
          id: TransferAccountTypeEnum.Bank,
          label: t(`common:${TransferAccountTypeEnum.Bank}`),
        },
        {
          id: TransferAccountTypeEnum.Cash,
          label: t(`common:${TransferAccountTypeEnum.Cash}`),
        },
      ]}
    />
  );

  const accountableSelect = React.useMemo(() => {
    switch (accountType as TransferAccountTypeEnum) {
      case TransferAccountTypeEnum.Bank:
        return <BankAccountSelectInput name={`${name}accountableId`} />;
      case TransferAccountTypeEnum.Cash:
        return <CashAccountSelectInput name={`${name}accountableId`} />;
      default:
        return null;
    }
  }, [accountType, name]);

  const amountInput = (
    <Input type="number" label={t('common:amount')} name={`${name}amount`} />
  );

  const descriptionInput = (
    <Input
      type="text"
      label={t('common:description')}
      name={`${name}description`}
    />
  );

  const removeButton = !!props.onRemove && (
    <>
      <Separator gap={24} />
      <Button
        onClick={props.onRemove}
        isError
        startEnhancer={(color, size) => {
          return <TrashBin color={color} width={size} height={size} />;
        }}
        buttonType="secondary"
        style={{
          maxWidth: 180,
        }}
      >
        {t('common:delete')}
      </Button>
    </>
  );
  return (
    <CardContainer>
      <SectionContainer>
        <HalfContainer>{transactionAtInput}</HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>{accountTypeSelect}</HalfContainer>
        <HalfContainer>{accountableSelect}</HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>{amountInput}</HalfContainer>
        <HalfContainer>{descriptionInput}</HalfContainer>
      </SectionContainer>
      {removeButton}
    </CardContainer>
  );
}

export function OtherExpenseCreateCard() {
  const { t } = useTranslation();
  const { control } = useFormContext<OtherExpenseCreateFormType>();
  const name = 'otherExpenses' as const;
  const { editable } = useFormState();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const addButton = editable && (
    <div>
      <Button
        style={{
          maxHeight: 34,
        }}
        buttonType="secondary"
        startEnhancer={(color, size) => {
          return <PlusCircleSVG color={color} width={size} height={size} />;
        }}
        onClick={() => {
          append({
            accountableId: null,
            accountType: null,
            amount: undefined,
            description: '',
            transactionAt: new Date(),
            acknowledgedAt: new Date(),
          } as unknown as OtherExpenseDetailCreateFormType);
        }}
      >
        {t('common:add')}
      </Button>
    </div>
  );

  return (
    <FullContainer
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {fields.map((field, index) => {
        const parentName = `${name}.${index}.` as const;
        return (
          <React.Fragment key={field.customId}>
            <ExpenseCard
              key={field.customId}
              name={parentName}
              onRemove={
                index === 0
                  ? undefined
                  : () => {
                      remove(index);
                    }
              }
            />
          </React.Fragment>
        );
      })}
      {addButton}
    </FullContainer>
  );
}

export default function OtherExpenseCard(props: { type: 'create' | 'view' }) {
  switch (props.type) {
    case 'create':
      return <OtherExpenseCreateCard />;
    case 'view':
      return (
        <FullContainer>
          <ExpenseCard isEdit />
        </FullContainer>
      );
  }
}
